export interface ProtocolSide {
  side: string
  points: string[]
  class: 'sp1' | 'sp2' | 'sp3'
}

export interface Protocol {
  id: string
  abbr: string
  title: string
  sick: string
  badge: string
  theory: string
  sides: ProtocolSide[]
}

export const clinicalDataRecord: Record<string, Omit<Protocol, 'id'>> = {
  md_back: {
    abbr: '$1M',
    title: 'Million Dollar Combo for Back Pain',
    sick: 'Lumbar / Sciatica',
    badge: 'MILLION DOLLAR',
    theory:
      'Treats lower back pain, sciatica, lumbar disc problems, and chronic back pain. Strengthens kidneys, moves blood stasis, relaxes lower back muscles. Alternate sides between treatments.',
    sides: [
      {
        side: 'Opposite Side',
        points: [
          'GB 39',
          'BL 57',
          'BL 60',
          'KI 6',
          'GB 41',
          'TH 5',
          'SI 3',
          'BL 62',
        ],
        class: 'sp1',
      },
    ],
  },
  md_neck: {
    abbr: '$1M',
    title: 'Million Dollar Combo for Neck Pain',
    sick: 'Cervical Spine / Whiplash',
    badge: 'MILLION DOLLAR',
    theory:
      'Treats neck pain, cervical problems, whiplash, neck stiffness. Opens yang qiao and yin qiao mai. Critical: Never needle the painful side. Use healthy side only.',
    sides: [
      {
        side: 'Healthy Side Only',
        points: [
          'BL 60',
          'KI 6',
          'GB 41',
          'TH 5',
          'SI 3',
          'BL 62',
          'GB 39',
          'BL 57',
        ],
        class: 'sp2',
      },
    ],
  },
  md_shoulder: {
    abbr: '$1M',
    title: 'Million Dollar Combo for Shoulder Pain',
    sick: 'Shoulder impingement / Frozen Shoulder',
    badge: 'MILLION DOLLAR',
    theory:
      'Treats shoulder pain, frozen shoulder, rotator cuff injury, shoulder impingement. Opens all eight extraordinary vessels. Moves qi and blood in shoulder region.',
    sides: [
      {
        side: 'Opposite Side',
        points: ['BL 60', 'KI 6', 'GB 41', 'TH 5', 'SI 3', 'BL 62'],
        class: 'sp2',
      },
    ],
  },
  md_fibro: {
    abbr: '$1M',
    title: 'Million Dollar Combo for Fibromyalgia',
    sick: 'Systemic Widespread Pain',
    badge: 'MILLION DOLLAR',
    theory:
      'Treats fibromyalgia, widespread body pain, chronic fatigue, tender points. Regulates all extraordinary vessels. Treats systemic yang qi deficiency. Use bilateral approach for systemic conditions.',
    sides: [
      {
        side: 'Bilateral',
        points: ['BL 60', 'KI 6', 'GB 41', 'TH 5', 'SI 3', 'BL 62'],
        class: 'sp3',
      },
    ],
  },
  md_headache: {
    abbr: '$1M',
    title: 'Million Dollar Combo for Headache',
    sick: 'All Headaches',
    badge: 'MILLION DOLLAR',
    theory:
      'Treats all types of headaches, migraines, tension headaches, cluster headaches. Opens all extraordinary vessels. Can be used bilaterally for chronic patterns.',
    sides: [
      {
        side: 'Bilateral / Alternating',
        points: ['BL 60', 'KI 6', 'GB 41', 'TH 5', 'SI 3', 'BL 62'],
        class: 'sp3',
      },
    ],
  },
  '12_magic_heart': {
    abbr: '12M',
    title: '12 Magic Points for Heart',
    sick: 'Heart / Cardiovascular',
    badge: 'MAGIC POINTS',
    theory:
      'Treats heart disease, chest pain, palpitations, arrhythmia, angina, hypertension. Calms shen, regulates heart qi, nourishes heart yin. Use for all cardiovascular disorders.',
    sides: [
      {
        side: 'Bilateral',
        points: ['HT 7', 'PC 6', 'PC 7', 'PC 8', 'LR 3', 'KI 3'],
        class: 'sp3',
      },
    ],
  },
  '8_magic_digestion': {
    abbr: '8M',
    title: '8 Magic Points for Digestion',
    sick: 'Gastrointestinal Tract',
    badge: 'MAGIC POINTS',
    theory:
      'All digestive disorders, IBS, gastritis, bloating, poor appetite. Strengthens spleen qi, harmonizes liver and spleen. Master points for all digestive issues.',
    sides: [
      {
        side: 'Bilateral',
        points: ['ST 36', 'SP 3', 'SP 6', 'LR 3'],
        class: 'sp3',
      },
    ],
  },
  '4_magic_constipation': {
    abbr: '4M',
    title: '4 Magic Points for Constipation',
    sick: 'Large Intestine / Spleen',
    badge: 'MAGIC POINTS',
    theory:
      'Treats constipation, chronic constipation, elderly constipation, post-operative constipation. Strengthens spleen qi, moistens intestines. Very effective for chronic constipation.',
    sides: [{ side: 'Bilateral', points: ['ST 36', 'SP 6'], class: 'sp3' }],
  },
  '4_magic_diarrhea': {
    abbr: '4M',
    title: '4 Magic Points for Diarrhea',
    sick: 'Spleen / Intestines',
    badge: 'MAGIC POINTS',
    theory:
      'Treats diarrhea, loose stools, IBS with diarrhea, chronic diarrhea. Strengthens spleen qi, stops diarrhea. Same points as constipation but different needling technique.',
    sides: [{ side: 'Bilateral', points: ['ST 36', 'SP 6'], class: 'sp3' }],
  },
  '8_magic_shen': {
    abbr: '8M',
    title: '8 Magic Points for Shen',
    sick: 'Heart and Kidney Disconnect',
    badge: 'MAGIC POINTS',
    theory:
      'Treats anxiety, depression, insomnia, panic attacks, PTSD, emotional instability. Calms shen, nourishes heart and kidney yin. Master protocol for all emotional disorders.',
    sides: [
      {
        side: 'Bilateral',
        points: ['HT 7', 'Yin Tang (YT 7)', 'KI 3', 'SP 6'],
        class: 'sp3',
      },
    ],
  },
  '8_magic_obgyn': {
    abbr: '8M',
    title: '8 Magic Points for OB/GYN',
    sick: 'Chong and Ren Mai',
    badge: 'MAGIC POINTS',
    theory:
      'All gynecological problems, menstrual disorders, fertility, menopause. Regulates chong and ren mai. Master protocol for all women health issues.',
    sides: [
      {
        side: 'Bilateral',
        points: ['SP 6', 'LR 3', 'REN 3', 'KI 3'],
        class: 'sp3',
      },
    ],
  },
  '4_magic_menses': {
    abbr: '4M',
    title: '4 Magic Points for Menstrual Problems',
    sick: 'Liver / Blood',
    badge: 'MAGIC POINTS',
    theory:
      'Treats irregular periods, painful periods, amenorrhea, heavy bleeding. Regulates liver qi and blood. Essential points for all menstrual disorders.',
    sides: [{ side: 'Bilateral', points: ['SP 6', 'LR 3'], class: 'sp3' }],
  },
  dr_tan_sperm: {
    abbr: 'MEN',
    title: 'Dr. Tans Points for Sperm',
    sick: 'Kidney Yang / Essence',
    badge: 'MENS HEALTH',
    theory:
      'Treats male infertility, low sperm count, poor sperm motility, impotence. Tonifies kidney yang and essence. Specific protocol for male reproductive health.',
    sides: [
      {
        side: 'Bilateral / Core',
        points: ['KI 3', 'BL 23', 'GV 4', 'REN 4'],
        class: 'sp3',
      },
    ],
  },
  pain_protocol: {
    abbr: '123',
    title: 'Balance Method Pain Protocol',
    sick: 'Localized Pain',
    badge: 'CORE METHOD',
    theory:
      'Used for any localized pain. Use 5 systems to find balancing meridian. Palpate for ashi points. Never needle local pain area.',
    sides: [
      {
        side: 'Opposite Side / Hologram',
        points: ['Find Ashi points on balancing meridian'],
        class: 'sp2',
      },
    ],
  },
  meno_gyno_pattern: {
    abbr: 'GYN',
    title: 'Menopause and All Gyno Pattern',
    sick: 'Endocrine / Reproductive',
    badge: 'WOMENS HEALTH',
    theory:
      'Female disorders, including menopause, PMS, emotional instability, fibroids, PCOS and all reproductive and sexual dysfunctions. Normalizes hormones.',
    sides: [
      {
        side: 'Left Side',
        points: [
          'TH 3',
          'TH 5',
          'LI 4',
          'LI 5',
          'SP 6',
          'SP 9',
          'KI 4',
          'KI 7',
        ],
        class: 'sp2',
      },
      {
        side: 'Right Side',
        points: [
          'HT 5',
          'PC 6',
          'PC 7',
          'GB 34',
          'GB 41',
          'ST 36',
          'ST 37',
          'ST 40',
        ],
        class: 'sp1',
      },
    ],
  },
  heat_pattern: {
    abbr: 'HEAT',
    title: 'Heat Pattern',
    sick: 'Systemic Heat / Inflammation',
    badge: 'GLOBAL BALANCE',
    theory:
      'Treats infections, inflammation, fever, red/hot/swollen conditions, acute inflammation. Rapid, forceful pulse. Clears heat, reduces inflammation.',
    sides: [
      {
        side: 'Left Side',
        points: ['LI 4', 'LI 11', 'ST 44', 'ST 45'],
        class: 'sp2',
      },
      {
        side: 'Right Side',
        points: ['LU 10', 'LU 11', 'SP 2', 'SP 3'],
        class: 'sp1',
      },
    ],
  },
  menopause_pattern: {
    abbr: 'MENO',
    title: 'Menopause Pattern',
    sick: 'Hormonal Imbalance',
    badge: 'GLOBAL BALANCE',
    theory:
      'All gynecological issues, menopause, hot flashes, night sweats, hormonal imbalance. Thin, rapid pulse. Regulates hormones, calms shen, balances yin-yang.',
    sides: [
      {
        side: 'Side A',
        points: ['TH 1', 'TH 3', 'SP 1', 'SP 6', 'LR 3'],
        class: 'sp2',
      },
      {
        side: 'Side B',
        points: ['PC 7', 'HT 7', 'ST 36', 'GB 34', 'GB 41'],
        class: 'sp1',
      },
    ],
  },
  jueyin_shaoyang: {
    abbr: 'JY-SY',
    title: 'Jueyin Shaoyang Pattern',
    sick: 'Wood Imbalance / Internal Wind',
    badge: 'PATTERN',
    theory:
      'Migraines, TMJ, lateral body pain, anxiety with anger, fibromyalgia, Type A personality. Wiry pulse, red tongue with yellow coat. Liver fire rising with gallbladder qi stagnation.',
    sides: [
      {
        side: 'Bilateral',
        points: ['LR 3', 'GB 34', 'GB 41', 'TH 5', 'PC 6', 'LR 14'],
        class: 'sp3',
      },
    ],
  },
  jueyin_yangming: {
    abbr: 'JY-YM',
    title: 'Jueyin Yangming Pattern',
    sick: 'Liver Invading Stomach',
    badge: 'PATTERN',
    theory:
      'IBS, acid reflux, anxiety with digestive upset, stress eating, epigastric pain. Rapid pulse, red tongue. Liver invading spleen with stomach heat.',
    sides: [
      {
        side: 'Bilateral',
        points: ['LR 3', 'ST 36', 'ST 6', 'PC 6', 'LR 13', 'ST 25'],
        class: 'sp3',
      },
    ],
  },
  shaoyin_shaoyang: {
    abbr: 'SY-SY',
    title: 'Shaoyin Shaoyang Pattern',
    sick: 'Metal/Earth Imbalance',
    badge: 'PATTERN',
    theory:
      'Insomnia, depression, burnout, chronic fatigue, night sweats, anxiety. Thin rapid pulse, red tongue with little coat. Heart-kidney yin deficiency with liver yang rising.',
    sides: [
      {
        side: 'Bilateral',
        points: ['HT 7', 'KI 3', 'GB 34', 'TH 5', 'Yin Tang (YT 7)', 'KI 6'],
        class: 'sp3',
      },
    ],
  },
  shaoyin_taiyang: {
    abbr: 'SY-TY',
    title: 'Shaoyin Taiyang Pattern',
    sick: 'Water/Fire Deficiency',
    badge: 'PATTERN',
    theory:
      'Lower back pain, sciatica, kidney deficiency, chronic fatigue, cold limbs.',
    sides: [
      {
        side: 'Bilateral',
        points: ['KI 3', 'BL 23', 'HT 7', 'SI 3', 'BL 62', 'KI 6'],
        class: 'sp3',
      },
    ],
  },
  taiyin_yangming: {
    abbr: 'TY-YM',
    title: 'Taiyin Yangming Pattern',
    sick: 'Earth/Metal Imbalance',
    badge: 'PATTERN',
    theory:
      'Respiratory + digestive problems, asthma with bloating, allergies with poor appetite.',
    sides: [
      {
        side: 'Bilateral',
        points: ['LU 5 (or 9)', 'LI 3 (or 11)', 'ST 36 (or 43)', 'SP 3 (or 9)'],
        class: 'sp3',
      },
    ],
  },
  taiyin_taiyang: {
    abbr: 'TY-TY',
    title: 'Taiyin Taiyang Pattern',
    sick: 'Dampness Accumulation',
    badge: 'PATTERN',
    theory:
      'Edema, obesity, water retention, chronic diarrhea, dampness accumulation.',
    sides: [
      {
        side: 'Bilateral',
        points: ['LU 7', 'SP 9', 'SI 3', 'BL 62', 'BL 20', 'SP 6'],
        class: 'sp3',
      },
    ],
  },
  yangming_heat: {
    abbr: 'YM-H',
    title: 'Yangming In Heat Pattern',
    sick: 'Yangming Heat',
    badge: 'PATTERN',
    theory:
      'High fever, infection, inflammation, acute gastritis, constipation with heat signs.',
    sides: [
      {
        side: 'Bilateral',
        points: ['LI 4', 'LI 11', 'ST 44', 'ST 45'],
        class: 'sp3',
      },
    ],
  },
  shaoyang_heat: {
    abbr: 'SY-H',
    title: 'Shaoyang In Heat Pattern',
    sick: 'Shaoyang Heat',
    badge: 'PATTERN',
    theory:
      'Alternating fever/chills, lateral headache, bitter taste, gallbladder inflammation. Wiry rapid pulse, red tongue with yellow sides. Shaoyang heat with qi stagnation.',
    sides: [
      {
        side: 'Bilateral',
        points: ['GB 34', 'TH 5', 'GB 41', 'TH 23', 'GB 20', 'TH 17'],
        class: 'sp3',
      },
    ],
  },
  taiyang_heat: {
    abbr: 'TY-H',
    title: 'Taiyang In Heat Pattern',
    sick: 'Taiyang / Bladder Heat',
    badge: 'PATTERN',
    theory:
      'UTI, kidney stones, cystitis, posterior headache with heat signs. Rapid pulse, red tongue with yellow coat. Bladder damp heat with small intestine fire.',
    sides: [
      {
        side: 'Bilateral',
        points: ['BL 23', 'SI 3', 'BL 28', 'SI 8', 'BL 40', 'SI 5'],
        class: 'sp3',
      },
    ],
  },
  case_cystitis: {
    abbr: 'CASE',
    title: 'Case: Interstitial Cystitis',
    sick: 'Foot Jue Yin (LV), Foot Shao Yin (KD)',
    badge: 'CASE STUDY',
    theory:
      '10/10 burning pain at vulva/urethra. Balanced using Jue Yin/Yang Ming conversions and Du vessel support to treat underlying anxiety.',
    sides: [
      { side: 'Right Side', points: ['PC 6', 'PC 8', 'ST 43'], class: 'sp1' },
      {
        side: 'Left Side',
        points: ['LI 1-5', 'LV 2, 3, 5', 'KD 2, 3, 7'],
        class: 'sp2',
      },
      { side: 'Head', points: ['DU 26', 'An Mian 5'], class: 'sp3' },
    ],
  },
}

export const clinicalData: Protocol[] = Object.entries(clinicalDataRecord)
  .map(([id, data]) => ({
    id,
    ...data,
  }))
  .sort((a, b) => a.title.localeCompare(b.title))
